/**
 * AI CONTEXT
 * Purpose: shared presentation primitives for square artwork tile surfaces —
 *   the exact-px tile grid, the letterboxing square tile shell, the tile-size
 *   slider, browser-fullscreen state, and the in-app lightbox. Extracted from
 *   the student profile's Artwork tab so Quality Check renders the identical
 *   controls (owner ask, Sep 2026). Both consumers stay independent of the
 *   ArtworkWall component; these primitives own layout and chrome only and
 *   never read Firestore.
 * Referenced by: StudentArtworkTilesSection (Artwork tab), QualityCheckView.
 */
import { Modal, Slider, Typography } from "antd"
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react"
import styled from "styled-components"

import Flex from "#blocks/Flex"
import { COLORS } from "#styles/colors"
import {
  RENDITION_LONG_EDGE,
  RENDITION_TIER,
  type RenditionTier,
} from "#utils/artworkRenditionHelpers"

const { Text } = Typography

/**
 * Tile edge in px. Each surface has its own slider and its own default:
 * grids start at thumbnail size (typical usage is scanning), the fullscreen
 * gallery starts at max (it exists to look closely). Neither is persisted —
 * the defaults are the point; user settings can come later if anyone asks.
 */
export const MIN_TILE_WIDTH = 60
export const MAX_TILE_WIDTH = 1000
export const THUMBNAIL_TILE_WIDTH = 140
export const TILE_WIDTH_STEP = 10
export const TILE_GAP_PX = 4
/** The slider control's own width cap, so it never stretches across the screen. */
export const SLIDER_MAX_WIDTH_PX = 300

/**
 * The fullscreen gallery's dark background (COLORS.GRAY7 darkened 20%). Most
 * pieces are on white paper, so the dark gray makes them stand out; the tile
 * squares share the color so only the artwork itself reads. Light surfaces
 * stay light.
 */
export const FULLSCREEN_BG = "#4A4B5A"

/**
 * Tiles below the thumb rendition's long edge are served by `_thumb.jpg`;
 * larger ones need `_med.jpg` or they render visibly soft.
 */
export const getTierForTileWidth = (tileWidth: number): RenditionTier =>
  tileWidth <= RENDITION_LONG_EDGE[RENDITION_TIER.THUMB]
    ? RENDITION_TIER.THUMB
    : RENDITION_TIER.MED

/**
 * `repeat(auto-fill, <w>px)` rather than `1fr` columns: the slider sets an
 * exact pixel width, so tiles must not stretch to fill the last row.
 * `$rowGap` and `$columnGap` override the default gap. Quality Check passes
 * 0 so the letterbox gray does not stripe between pictures.
 */
export const STileGrid = styled.div<{
  $tileWidth: number
  $rowGap?: number
  $columnGap?: number
}>`
  display: grid;
  grid-template-columns: repeat(auto-fill, ${({ $tileWidth }) => $tileWidth}px);
  justify-content: start;
  gap: ${({ $rowGap }) => $rowGap ?? TILE_GAP_PX}px
    ${({ $columnGap }) => $columnGap ?? TILE_GAP_PX}px;
`

/**
 * Hover/focus-revealed controls in the tile's top-right corner; the square
 * shell's `.artwork-tile-actions` rule fades them in. Hidden until hover so
 * a dense wall of tiles stays readable.
 */
export const STileActions = styled.div`
  position: absolute;
  right: 4px;
  top: 4px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
`

/**
 * The square shell an artwork letterboxes inside (`object-fit: contain`,
 * centered) so nothing is cover-cropped, while the square box keeps rows
 * evenly spaced. Hover/focus reveals any `.artwork-tile-actions` child.
 */
export const STileSquare = styled.div<{ $dark: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: ${({ $dark }) => ($dark ? FULLSCREEN_BG : COLORS.GRAY2)};

  &:hover .artwork-tile-actions,
  &:focus-within .artwork-tile-actions {
    opacity: 1;
    pointer-events: auto;
  }
`

/** The tile's click target: an unstyled button filling the square. */
export const STileOpenButton = styled.button`
  all: unset;
  display: block;
  width: 100%;
  height: 100%;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${COLORS.theme.colorPrimary};
    outline-offset: -2px;
  }
`

/**
 * Each surface scrolls this container — not the whole tab or modal — so the
 * sticky controls stay pinned while the tiles move underneath.
 */
export const SScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`

export const SStickyControls = styled.div<{ $dark: boolean }>`
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Top padding lives here, not on the scroll container: a sticky element
     cannot rise into its container's padding, which would leave a strip the
     tiles visibly scroll through. */
  padding: 10px 0;
  background: ${({ $dark }) => ($dark ? FULLSCREEN_BG : "#fff")};
`

export const secondaryTextStyle = (dark: boolean): CSSProperties | undefined =>
  dark ? { color: COLORS.GRAY3 } : undefined

export const isQuarterTurn = (rotation: number): boolean => rotation % 180 !== 0

/**
 * Slider labels read as primary text, not secondary chrome: the control was
 * easy to miss in the filter-heavy headers (owner ask, Sep 2026), so the
 * labels are bold full-contrast and the rail/track carry stronger colors
 * than antd's pale defaults.
 */
const sliderLabelStyle = (dark: boolean): CSSProperties => ({
  fontSize: 13,
  whiteSpace: "nowrap",
  color: dark ? "#fff" : undefined,
})

export const TileWidthSlider = ({
  tileWidth,
  dark,
  onChange,
  testId = "artwork-tiles-size-slider",
}: {
  tileWidth: number
  dark: boolean
  onChange: (value: number) => void
  testId?: string
}) => (
  <Flex
    gap={8}
    style={{
      alignItems: "center",
      minWidth: 200,
      maxWidth: SLIDER_MAX_WIDTH_PX,
      flex: 1,
    }}
  >
    <Text strong style={sliderLabelStyle(dark)}>
      Size
    </Text>
    {/* Wrapped: antd's slider does not forward data-* attributes. */}
    <span style={{ flex: 1 }} data-testid={testId} data-tile-width={tileWidth}>
      <Slider
        min={MIN_TILE_WIDTH}
        max={MAX_TILE_WIDTH}
        step={TILE_WIDTH_STEP}
        value={tileWidth}
        onChange={onChange}
        tooltip={{ formatter: (value) => `${value ?? tileWidth}px` }}
        style={{ margin: 0 }}
        styles={{
          rail: {
            background: dark
              ? "rgba(255, 255, 255, 0.35)"
              : "rgba(0, 0, 0, 0.2)",
          },
          track: { background: COLORS.theme.colorPrimary },
        }}
      />
    </span>
    <Text strong style={sliderLabelStyle(dark)}>
      {tileWidth}px
    </Text>
  </Flex>
)

/**
 * Hands the browser's native fullscreen back. Guarded because
 * `exitFullscreen` rejects when nothing is fullscreen, and the rejection of a
 * user-initiated exit (Esc) is not actionable either way.
 */
export const exitNativeFullscreen = (): void => {
  if (document.fullscreenElement) {
    void document.exitFullscreen().catch(() => undefined)
  }
}

/**
 * Browser-fullscreen state for a gallery modal. Alongside the modal, asks the
 * browser for real fullscreen (hides tabs, URL bar, bookmarks). A rejection
 * is deliberately ignored: the modal still works windowed, and the next
 * button press simply asks again. The browser reserves Esc to exit its
 * fullscreen first; closing the modal along with it means one Esc backs out
 * of everything instead of stranding the gallery in a normal window.
 *
 * Native fullscreen belongs to the gallery, so it is released on EVERY path
 * that takes the gallery off screen — including the ones no handler runs on:
 * a surface's loading/empty early returns (pass them through `canShow`) and
 * unmount. `closeFullscreen` therefore only sets state; the release effect
 * owns `exitFullscreen`.
 */
export const useBrowserFullscreen = (canShow: boolean = true) => {
  const [isFullscreen, setIsFullscreen] = useState(false)

  const openFullscreen = useCallback(() => {
    setIsFullscreen(true)
    void document.documentElement.requestFullscreen().catch(() => undefined)
  }, [])

  const closeFullscreen = useCallback(() => setIsFullscreen(false), [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) setIsFullscreen(false)
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
  }, [])

  const isOpen = isFullscreen && canShow

  useEffect(() => {
    if (isOpen) return
    exitNativeFullscreen()
    setIsFullscreen(false)
  }, [isOpen])

  useEffect(() => () => exitNativeFullscreen(), [])

  return { isFullscreen: isOpen, openFullscreen, closeFullscreen }
}

/** The lightbox fills the viewport; clicking the picture closes it. */
const SLightboxBody = styled.div<{ $editing: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: calc(100vh - 110px);
  cursor: ${(p) => (p.$editing ? "default" : "zoom-out")};
`

/** Always visible (unlike the tiles' hover actions) — there is one image. */
const SLightboxActions = styled.div`
  position: absolute;
  right: 8px;
  top: 8px;
  display: flex;
  gap: 6px;
`

/**
 * In-app lightbox: the full-quality image on the dark background. Clicking a
 * tile must never send the user out of the app, so this replaces any
 * open-in-new-tab behavior. Clicking the image (or anywhere in the body)
 * closes it; clicks on `actions` do not.
 */
export const ArtworkLightbox = ({
  open,
  artworkId,
  src,
  caption,
  rotation,
  animateRotation = false,
  onClose,
  actions,
  editor,
  empty,
  onImageError,
}: {
  open: boolean
  /** For the `data-artwork-id` hook; null while nothing is open. */
  artworkId: string | null
  src: string
  caption: string
  /** Display rotation in degrees; cumulative (>360°) values are fine. */
  rotation: number
  /** Same rule as the tiles: animate pending clicks, swap saves instantly. */
  animateRotation?: boolean
  onClose: () => void
  /** Always-visible top-right controls, e.g. rotate / add-to-portfolio. */
  actions?: ReactNode
  /**
   * Crop tools, drawn in place of the picture. Clicks here do not close the
   * lightbox — Cancel and Save belong to the editor.
   */
  editor?: ReactNode
  /** Shown when `src` is empty or the full image fails to load. */
  empty?: ReactNode
  /**
   * The full image failed. The view stays open and shows `empty`. The URL is
   * the one that failed, so a parent can hide that piece.
   */
  onImageError?: (src: string) => void
}) => {
  // Natural pixel size of the loaded image, keyed by src so switching pieces
  // never applies a stale cap.
  const [loadedSize, setLoadedSize] = useState<{
    src: string
    width: number
    height: number
  } | null>(null)
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null)

  const naturalSize = loadedSize?.src === src ? loadedSize : null
  const imageBroken = Boolean(src) && brokenSrc === src

  // Stretch to fill the viewport, but never past the image's native
  // resolution (the caps below); object-fit keeps the aspect either way.
  // A quarter turn swaps the fitted axes: the rotated width is the image
  // height, so the stretch targets swap too or the piece overflows.
  const sizeStyle = isQuarterTurn(rotation)
    ? {
        width: "calc(100vh - 140px)",
        height: "calc(96vw - 60px)",
        maxWidth: naturalSize ? `${naturalSize.width}px` : undefined,
        maxHeight: naturalSize ? `${naturalSize.height}px` : undefined,
      }
    : {
        width: "100%",
        height: "100%",
        maxWidth: naturalSize ? `${naturalSize.width}px` : undefined,
        maxHeight: naturalSize ? `${naturalSize.height}px` : undefined,
      }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width="98vw"
      style={{ top: 8, maxWidth: "none", paddingBottom: 0 }}
      styles={{
        body: { background: FULLSCREEN_BG, padding: 8 },
      }}
      title={caption}
    >
      {open && artworkId ? (
        <SLightboxBody
          $editing={!!editor}
          onClick={editor ? undefined : onClose}
          data-testid="artwork-lightbox"
          data-artwork-id={artworkId}
        >
          {editor ? (
            <div
              onClick={(event) => event.stopPropagation()}
              style={{ width: "100%", height: "100%" }}
            >
              {editor}
            </div>
          ) : src && !imageBroken ? (
            <img
              src={src}
              alt={caption}
              draggable={false}
              onError={() => {
                setBrokenSrc(src)
                onImageError?.(src)
              }}
              onLoad={(event) =>
                setLoadedSize({
                  src,
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                })
              }
              style={{
                ...sizeStyle,
                objectFit: "contain",
                transform: rotation ? `rotate(${rotation}deg)` : undefined,
                transition: animateRotation ? "transform 0.2s ease" : undefined,
              }}
            />
          ) : (
            empty
          )}
          {/* stopPropagation: the body click closes the lightbox. */}
          {actions && !editor ? (
            <SLightboxActions onClick={(event) => event.stopPropagation()}>
              {actions}
            </SLightboxActions>
          ) : null}
        </SLightboxBody>
      ) : null}
    </Modal>
  )
}
